import { useState } from 'react';
import { Button, BUTTON_SIZES } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText, TextArea } from 'ui/inputs';
import { Modal, ModalFooter, ModalHeader } from 'ui/modal';
import { localizeDateTime, DateFormat, TimeFormat } from 'utils/l10n/datetime';

import { StyledInputWrapper, StyledModalBody } from '../Styled';
import { StyledInputLabelOptional, StyledListWrapper } from './Styled';
import { useSaveAsNewListMutation, useTranslation } from '../../../hooks';
import { AIQuerySummaryInputButton } from './AIQuerySummaryInputButton';
import { useModalsContext } from '../../../contexts/modals';
import { SaveAsNewListParams } from '../../../types';
import { useSegmentationQueryContext } from '../../../contexts/segmentationQuery';
import { usePermissionsContext } from '../../../contexts/permissions';

export const testIds = {
  bannerInfo: 'segmentation-ndl-banner-info',
  buttonSave: 'segmentation-ndl-btn-save',
  inputDescription: 'segmentation-ndl-input-desc',
  inputName: 'segmentation-ndl-input-name',
  modal: 'segmentation-ndl',
};

export const NewDynamicListModal = () => {
  const { t } = useTranslation();
  const { canUseSegmentationAiDescriptions } = usePermissionsContext();
  const { searchQuery } = useSegmentationQueryContext();
  const { hideModal: closeModal } = useModalsContext();
  const { mutate: saveAsNewDynamicList, isLoading } = useSaveAsNewListMutation();
  const [name, setName] = useState<string>(
    t('modal.newDynamicList.field.listName.defaultValue', {
      dateTime: localizeDateTime(new Date(), {
        dateStyle: DateFormat.Short,
        showTime: true,
        timeStyle: TimeFormat.TwelveHour,
      }),
    }),
  );
  const [description, setDescription] = useState<string>('');

  const canSubmit = !!name.trim().length;

  const addDynamicList = () => {
    if (isLoading) return;
    const params: SaveAsNewListParams = {
      description,
      isDynamic: true,
      name,
      searchQuery,
      subLists: [],
    };

    saveAsNewDynamicList(params);
    closeModal();
  };

  return (
    <Modal id={testIds.modal} onClose={closeModal} isModalOpen>
      <ModalHeader>{t('modal.newDynamicList.header')}</ModalHeader>
      <StyledModalBody>
        <StyledListWrapper>
          <StyledInputWrapper>
            <FormField
              hasError={!name.trim().length}
              labelText={t('modal.newDynamicList.field.listName.label')}
              restrictionText={`${name.length}/255`}
            >
              <InputText
                data-test={testIds.inputName}
                hasIcon
                maxLength={255}
                onChange={setName}
                value={name}
              />
            </FormField>
          </StyledInputWrapper>
          <StyledInputWrapper>
            <FormField
              labelText={
                <>
                  {t('modal.newDynamicList.field.description.label')}&nbsp;
                  <StyledInputLabelOptional>
                    {t('modal.newDynamicList.field.optional')}
                  </StyledInputLabelOptional>
                </>
              }
              restrictionText={`${description?.length || 0}/255`}
            >
              <TextArea
                data-test={testIds.inputDescription}
                maxLength={255}
                onChange={setDescription}
                placeholder={t('modal.newDynamicList.field.description.placeholder')}
                rows={3}
                value={description}
                {...(canUseSegmentationAiDescriptions
                  ? {
                      Suffix: (
                        <AIQuerySummaryInputButton onCommit={setDescription} query={searchQuery} />
                      ),
                    }
                  : {})}
              />
            </FormField>
          </StyledInputWrapper>
        </StyledListWrapper>
      </StyledModalBody>
      <ModalFooter>
        <Button
          isDisabled={!canSubmit}
          onClick={addDynamicList}
          size={BUTTON_SIZES.LARGE}
          data-test={testIds.buttonSave}
        >
          {t('modal.newDynamicList.button.save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
